<template>
    <form
        ref="formRef"
        class="s-form"
        :class="formClass"
        @submit.prevent
    >
        <slot />
    </form>
</template>

<script setup lang="ts">
    import { computed, provide, reactive, ref, toRef, watch } from 'vue'
    import type { FormContext, FormExpose, FormItemContext, FormProps, FormRule, ValidateResult } from './form.types'
    import { FORM_CONTEXT_KEY } from './form.types'

    defineOptions({ name: 's-form' })

    const props = withDefaults(defineProps<FormProps>(), {
        labelPosition: 'right',
        disabled: false,
        showRequiredAsterisk: true,
        scrollToError: true,
        trimWhitespace: true,
    })

    const emit = defineEmits<{
        validate: [prop: string, valid: boolean, message: string]
    }>()

    // 表单引用
    const formRef = ref<HTMLFormElement>()

    // 存储所有注册的表单项
    const fields = reactive<Map<string, FormItemContext>>(new Map())

    // 存储字段错误信息
    const fieldErrors = reactive<Record<string, string>>({})

    // 存储字段初始值（用于重置）
    const initialValues = ref<Record<string, any>>({})

    // 计算样式类
    const formClass = computed(() => ({
        [`s-form--label-${props.labelPosition}`]: true,
        's-form--disabled': props.disabled,
    }))

    // 初始化时保存初始值
    watch(
        () => props.model,
        model => {
            if (model && Object.keys(initialValues.value).length === 0) {
                initialValues.value = JSON.parse(JSON.stringify(model))
            }
        },
        { immediate: true, deep: true }
    )

    /**
     * 获取字段的校验规则
     */
    function getFieldRules(prop: string): FormRule[] {
        const rules = props.rules?.[prop]
        if (!rules) return []
        return Array.isArray(rules) ? rules : [rules]
    }

    /**
     * 判断值是否为空（支持数组、对象等类型）
     */
    function isEmptyValue(value: any): boolean {
        if (value === undefined || value === null || value === '') {
            return true
        }
        // 字符串类型：根据 trimWhitespace 配置判断纯空格是否视为空值
        if (typeof value === 'string' && props.trimWhitespace && value.trim() === '') {
            return true
        }
        // 数组类型：空数组视为空值
        if (Array.isArray(value) && value.length === 0) {
            return true
        }
        // 对象类型：空对象视为空值（排除特殊对象如 Date）
        if (typeof value === 'object' && !(value instanceof Date) && Object.keys(value).length === 0) {
            return true
        }
        return false
    }

    /**
     * 执行单条规则校验
     */
    async function executeRule(value: any, rule: FormRule): Promise<string> {
        // 必填校验
        if (rule.required) {
            if (isEmptyValue(value)) {
                return rule.message || '此项为必填'
            }
        }

        // 空值不继续校验（非必填情况）
        if (isEmptyValue(value)) {
            return ''
        }

        // 最小长度
        if (rule.min !== undefined && String(value).length < rule.min) {
            return rule.message || `至少输入 ${rule.min} 个字符`
        }

        // 最大长度
        if (rule.max !== undefined && String(value).length > rule.max) {
            return rule.message || `最多输入 ${rule.max} 个字符`
        }

        // 正则校验
        if (rule.pattern && !rule.pattern.test(String(value))) {
            return rule.message || '格式不正确'
        }

        // 自定义校验
        if (rule.validator) {
            const result = await rule.validator(value, rule)
            if (typeof result === 'string') {
                return result
            }
            if (result === false) {
                return rule.message || '校验失败'
            }
        }

        return ''
    }

    /**
     * 校验单个字段
     */
    async function validateField(prop: string): Promise<{ valid: boolean; message: string }> {
        const value = props.model[prop]
        const rules = getFieldRules(prop)

        // 无规则直接通过
        if (rules.length === 0) {
            clearFieldError(prop)
            return { valid: true, message: '' }
        }

        // 依次执行规则
        for (const rule of rules) {
            const errorMessage = await executeRule(value, rule)
            if (errorMessage) {
                setFieldError(prop, errorMessage)
                emit('validate', prop, false, errorMessage)
                return { valid: false, message: errorMessage }
            }
        }

        clearFieldError(prop)
        emit('validate', prop, true, '')
        return { valid: true, message: '' }
    }

    /**
     * 校验整个表单
     */
    async function validate(): Promise<ValidateResult> {
        const result: ValidateResult = {
            valid: true,
            errors: {},
            fields: {},
        }

        const props_array = Array.from(fields.keys())
        let firstErrorProp: string | null = null

        for (const prop of props_array) {
            const { valid, message } = await validateField(prop)
            result.fields[prop] = { valid, message }
            if (!valid) {
                result.valid = false
                result.errors[prop] = message
                if (!firstErrorProp) {
                    firstErrorProp = prop
                }
            }
        }

        // 滚动到第一个错误
        if (!result.valid && props.scrollToError && firstErrorProp) {
            scrollToField(firstErrorProp)
        }

        return result
    }

    /**
     * 校验指定字段
     */
    async function validateFields(props_to_validate: string | string[]): Promise<ValidateResult> {
        const propList = Array.isArray(props_to_validate) ? props_to_validate : [props_to_validate]
        const result: ValidateResult = {
            valid: true,
            errors: {},
            fields: {},
        }

        for (const prop of propList) {
            if (fields.has(prop)) {
                const { valid, message } = await validateField(prop)
                result.fields[prop] = { valid, message }
                if (!valid) {
                    result.valid = false
                    result.errors[prop] = message
                }
            }
        }

        return result
    }

    /**
     * 重置表单
     */
    function resetFields(): void {
        // 重置值
        Object.keys(props.model).forEach(key => {
            if (key in initialValues.value) {
                props.model[key] = initialValues.value[key]
            } else {
                props.model[key] = ''
            }
        })

        // 清除所有错误
        Object.keys(fieldErrors).forEach(key => {
            delete fieldErrors[key]
        })

        // 调用各字段的重置方法
        fields.forEach(field => {
            field.resetField()
        })
    }

    /**
     * 清除校验状态
     */
    function clearValidate(props_to_clear?: string | string[]): void {
        if (!props_to_clear) {
            // 清除所有
            Object.keys(fieldErrors).forEach(key => {
                delete fieldErrors[key]
            })
            fields.forEach(field => {
                field.clearValidate()
            })
        } else {
            const propList = Array.isArray(props_to_clear) ? props_to_clear : [props_to_clear]
            propList.forEach(prop => {
                delete fieldErrors[prop]
                fields.get(prop)?.clearValidate()
            })
        }
    }

    /**
     * 滚动到指定字段
     */
    function scrollToField(prop: string): void {
        const formItem = formRef.value?.querySelector(`[data-prop="${prop}"]`)
        if (formItem) {
            formItem.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }

    /**
     * 设置字段错误
     */
    function setFieldError(prop: string, message: string): void {
        fieldErrors[prop] = message
    }

    /**
     * 清除字段错误
     */
    function clearFieldError(prop: string): void {
        delete fieldErrors[prop]
    }

    /**
     * 获取字段错误
     */
    function getFieldError(prop: string): string {
        return fieldErrors[prop] || ''
    }

    /**
     * 注册表单项
     */
    function registerField(field: FormItemContext): void {
        fields.set(field.prop, field)
    }

    /**
     * 注销表单项
     */
    function unregisterField(prop: string): void {
        fields.delete(prop)
        delete fieldErrors[prop]
    }

    // 提供上下文给子组件
    provide<FormContext>(FORM_CONTEXT_KEY, {
        model: toRef(props, 'model'),
        rules: toRef(props, 'rules'),
        labelWidth: toRef(props, 'labelWidth'),
        labelPosition: toRef(props, 'labelPosition'),
        disabled: toRef(props, 'disabled'),
        showRequiredAsterisk: toRef(props, 'showRequiredAsterisk'),
        trimWhitespace: toRef(props, 'trimWhitespace'),
        registerField,
        unregisterField,
        validateField,
        getFieldError,
        setFieldError,
        clearFieldError,
    })

    // 暴露方法
    defineExpose<FormExpose>({
        validate,
        validateField: validateFields,
        resetFields,
        clearValidate,
        scrollToField,
    })
</script>

<style scoped lang="scss">
    .s-form {
        width: 100%;

        // 标签在顶部
        &--label-top {
            :deep(.s-form-item__label) {
                display: block;
                margin-bottom: 8px;
                text-align: left;
            }
        }

        // 标签在左侧
        &--label-left {
            :deep(.s-form-item__label) {
                text-align: left;
            }
        }

        // 标签在右侧
        &--label-right {
            :deep(.s-form-item__label) {
                text-align: right;
            }
        }

        // 禁用状态
        &--disabled {
            :deep(input),
            :deep(select),
            :deep(textarea) {
                opacity: 0.6;
                cursor: not-allowed;
            }
        }
    }
</style>
